import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
  NoticeBanner,
  type V2GridColumn,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useAmountDisplay, useTenantSettings } from '../../lib/tenantSettings.js';
import { vatEnabled } from '../../lib/types/tenantSettings.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  FiscalYearListResponse,
  VatDirection,
  VatEntry,
  VatEntryListResponse,
  VatEntryStatus,
  VatFilingItem,
  VatFilingListResponse,
} from '../../lib/types/finance.js';
import {
  amountColumnHeader,
  amountUnitLabel,
  formatRialFa,
  journalHref,
  VAT_SOURCE_KIND_LABEL,
  vatSourceHref,
  vatStatusLabel,
} from '../../lib/finance-display.js';

const DIRECTION_FILTERS: ReadonlyArray<{ value: '' | VatDirection; label: string }> = [
  { value: '', label: 'همه' },
  { value: 'output', label: 'خروجی' },
  { value: 'input', label: 'ورودی' },
];

const STATUS_FILTERS: ReadonlyArray<{ value: '' | VatEntryStatus; label: string }> = [
  { value: '', label: 'همه' },
  { value: 'draft', label: 'پیش‌نویس' },
  { value: 'posted', label: 'ثبت‌شده' },
  { value: 'reversed', label: 'برگشت‌خورده' },
];

interface VatRow extends VatEntry {
  readonly id: string;
}

const QUARTER_NAMES: Record<number, string> = {
  1: 'بهار (سه ماهه اول)',
  2: 'تابستان (سه ماهه دوم)',
  3: 'پاییز (سه ماهه سوم)',
  4: 'زمستان (سه ماهه چهارم)',
};

export function VatPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const settings = useTenantSettings();
  const { unit } = useAmountDisplay();

  const [activeTab, setActiveTab] = useState<'entries' | 'filings'>('entries');
  const [direction, setDirection] = useState<'' | VatDirection>('');
  const [status, setStatus] = useState<'' | VatEntryStatus>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filingRefreshKey, setFilingRefreshKey] = useState(0);

  // Modals for Filing
  const [showPrepareModal, setShowPrepareModal] = useState(false);
  const [prepareFiscalYearId, setPrepareFiscalYearId] = useState('');
  const [prepareQuarter, setPrepareQuarter] = useState<number>(1);
  const [prepareYearJalali, setPrepareYearJalali] = useState<number>(1403);
  const [preparePeriodStart, setPreparePeriodStart] = useState('1403-01-01');
  const [preparePeriodEnd, setPreparePeriodEnd] = useState('1403-03-31');
  const [prepareFilingDeadline, setPrepareFilingDeadline] = useState('1403-04-15');

  const [submitFilingTarget, setSubmitFilingTarget] = useState<VatFilingItem | null>(null);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [payFilingTarget, setPayFilingTarget] = useState<VatFilingItem | null>(null);
  const [paymentReference, setPaymentReference] = useState('');

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const rangeQuery = [
    fromDate ? `fromDate=${encodeURIComponent(fromDate)}` : '',
    toDate ? `toDate=${encodeURIComponent(toDate)}` : '',
  ].filter(Boolean);
  const query = [
    direction ? `direction=${direction}` : '',
    status ? `status=${status}` : '',
    ...rangeQuery,
  ]
    .filter(Boolean)
    .join('&');
  const path = slug ? `${base}/api/v1/finance/vat/entries${query ? `?${query}` : ''}` : null;
  const result = useApiGet<VatEntryListResponse>(path, [slug, direction, status, fromDate, toDate]);

  // Summary: posted entries only, independent of the table's direction/status filters.
  const summaryQuery = ['status=posted', ...rangeQuery].join('&');
  const summary = useApiGet<VatEntryListResponse>(
    slug ? `${base}/api/v1/finance/vat/entries?${summaryQuery}` : null,
    [slug, fromDate, toDate],
  );

  // Filings list
  const filingsResult = useApiGet<VatFilingListResponse>(
    slug ? `${base}/api/v1/finance/vat/filings` : null,
    [slug, filingRefreshKey],
  );

  // Fiscal years for prepare dropdown
  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );

  const rows: VatRow[] = useMemo(() => {
    if (result.status !== 'ready') return [];
    return result.data.items.map((item) => ({ ...item, id: item.vatEntryId }));
  }, [result]);

  const totals = useMemo(() => {
    if (summary.status !== 'ready') return null;
    let outputVat = 0n;
    let inputVat = 0n;
    let outputBase = 0n;
    let inputBase = 0n;
    for (const e of summary.data.items) {
      if (e.direction === 'output') {
        outputVat += BigInt(e.vatAmountRial);
        outputBase += BigInt(e.taxableBaseRial);
      } else if (e.direction === 'input') {
        inputVat += BigInt(e.vatAmountRial);
        inputBase += BigInt(e.taxableBaseRial);
      }
    }
    return { outputVat, inputVat, outputBase, inputBase, payable: outputVat - inputVat };
  }, [summary]);

  if (!slug) return null;

  if (!vatEnabled(settings)) {
    return (
      <NoticeBanner tone="info">
        ارزش افزوده غیرفعال است. <Link to={`${base}/admin`}>تنظیمات</Link>
      </NoticeBanner>
    );
  }

  function handleQuarterChange(quarter: number, year: number) {
    setPrepareQuarter(quarter);
    setPrepareYearJalali(year);
    if (quarter === 1) {
      setPreparePeriodStart(`${year}-01-01`);
      setPreparePeriodEnd(`${year}-03-31`);
      setPrepareFilingDeadline(`${year}-04-15`);
    } else if (quarter === 2) {
      setPreparePeriodStart(`${year}-04-01`);
      setPreparePeriodEnd(`${year}-06-31`);
      setPrepareFilingDeadline(`${year}-07-15`);
    } else if (quarter === 3) {
      setPreparePeriodStart(`${year}-07-01`);
      setPreparePeriodEnd(`${year}-09-30`);
      setPrepareFilingDeadline(`${year}-10-15`);
    } else {
      setPreparePeriodStart(`${year}-10-01`);
      setPreparePeriodEnd(`${year}-12-29`);
      setPrepareFilingDeadline(`${year + 1}-01-15`);
    }
  }

  async function onPrepareFiling() {
    if (!prepareFiscalYearId) {
      setActionError('لطفاً سال مالی را انتخاب کنید.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/vat/filings/prepare`, {
        fiscalYearId: prepareFiscalYearId,
        quarter: prepareQuarter,
        yearJalali: prepareYearJalali,
        periodStart: preparePeriodStart,
        periodEnd: preparePeriodEnd,
        filingDeadline: prepareFilingDeadline,
      });
      setShowPrepareModal(false);
      setFilingRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(bannerMessageFrom(err, 'تهیه اظهارنامه با خطا مواجه شد.'));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitFiling() {
    if (!submitFilingTarget || !receiptNumber.trim()) {
      setActionError('شماره رهگیری یا قبض تسلیم سازمان مالیاتی الزامی است.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/vat/filings/${submitFilingTarget.filingId}/submit`, {
        receiptNumber: receiptNumber.trim(),
      });
      setSubmitFilingTarget(null);
      setReceiptNumber('');
      setFilingRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(bannerMessageFrom(err, 'ثبت تسلیم اظهارنامه ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  async function onPayFiling() {
    if (!payFilingTarget || !paymentReference.trim()) {
      setActionError('شناسه پرداخت یا شماره فیش بانکی الزامی است.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/vat/filings/${payFilingTarget.filingId}/pay`, {
        paymentReference: paymentReference.trim(),
      });
      setPayFilingTarget(null);
      setPaymentReference('');
      setFilingRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(bannerMessageFrom(err, 'ثبت پرداخت مالیات ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  const columns: readonly V2GridColumn<VatRow>[] = [
    {
      key: 'direction',
      header: 'جهت',
      render: (row) => (
        <V2Badge tone={row.direction === 'output' ? 'info' : 'neutral'}>
          {row.directionLabel}
        </V2Badge>
      ),
    },
    {
      key: 'source',
      header: 'منبع',
      render: (row) => VAT_SOURCE_KIND_LABEL[row.sourceKind] ?? 'منبع ثبت‌شده',
    },
    {
      key: 'base',
      header: amountColumnHeader('مأخذ', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.taxableBaseRial, unit)}</span>,
    },
    {
      key: 'vat',
      header: amountColumnHeader('مالیات', unit),
      align: 'end',
      emphasis: 'key',
      render: (row) => <span className="sk-num">{formatRialFa(row.vatAmountRial, unit)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge
          tone={
            row.status === 'posted' ? 'success' : row.status === 'reversed' ? 'warn' : 'neutral'
          }
        >
          {vatStatusLabel(row.status)}
        </V2Badge>
      ),
    },
    {
      key: 'date',
      header: 'تاریخ',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>,
    },
    {
      key: 'links',
      header: '',
      hideable: false,
      render: (row) => {
        const sourceHref = vatSourceHref(base, row.sourceKind, row.sourceId);
        return (
          <>
            <Link to={journalHref(base, row.journalId)}>سند حسابداری</Link>
            {sourceHref ? (
              <>
                {' · '}
                <Link to={sourceHref}>قلم باز</Link>
              </>
            ) : null}
          </>
        );
      },
    },
  ];

  const rangeLabel =
    fromDate || toDate
      ? `${fromDate ? `از ${displayJalaliDate(fromDate)}` : ''}${fromDate && toDate ? ' ' : ''}${toDate ? `تا ${displayJalaliDate(toDate)}` : ''}`
      : 'همهٔ تاریخ‌ها';

  const summaryRow = (label: string, value: string, color?: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
      <span style={{ color: 'var(--sk-fg-muted)' }}>{label}</span>
      <span className="sk-num" style={{ fontWeight: 600, ...(color ? { color } : {}) }}>
        {value}
      </span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header View Switcher */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className={`sk-btn ${activeTab === 'entries' ? 'sk-btn--primary' : 'sk-btn--secondary'} sk-btn--sm`}
            onClick={() => setActiveTab('entries')}
          >
            ثبت‌های ارزش افزوده (دفتر)
          </button>
          <button
            type="button"
            className={`sk-btn ${activeTab === 'filings' ? 'sk-btn--primary' : 'sk-btn--secondary'} sk-btn--sm`}
            onClick={() => setActiveTab('filings')}
          >
            اظهارنامه‌های فصلی و تسلیم (Filing)
          </button>
        </div>

        {activeTab === 'filings' ? (
          <V2Button
            type="button"
            size="sm"
            variant="primary"
            onClick={() => {
              if (years.status === 'ready' && years.data.items[0]) {
                setPrepareFiscalYearId(years.data.items[0].fiscalYearId);
              }
              handleQuarterChange(1, 1403);
              setShowPrepareModal(true);
            }}
          >
            + تهیه اظهارنامه فصلی جدید
          </V2Button>
        ) : null}
      </div>

      {actionError ? <NoticeBanner tone="warning">{actionError}</NoticeBanner> : null}

      {/* VIEW 1: OPERATIONAL VAT ENTRIES */}
      {activeTab === 'entries' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
          <div
            style={{
              flex: '2 1 600px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {result.status === 'loading' ? <LoadingState /> : null}
            {result.status === 'error' ? (
              <StatePanel
                variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
                title={
                  result.error.status === 403 ? 'دسترسی ندارید' : 'ثبت‌های مالیات قابل نمایش نیست'
                }
              />
            ) : null}
            {result.status === 'ready' ? (
              <V2DataGrid
                icon="invoice"
                title="ثبت‌های مالیات بر ارزش افزوده"
                subtitle={`${rows.length.toLocaleString('fa-IR')} مورد · ${rangeLabel}`}
                densitySwitch={false}
                filters={[
                  {
                    id: 'direction',
                    label: 'جهت',
                    value: direction,
                    options: DIRECTION_FILTERS,
                    onChange: (v) => setDirection(v as '' | VatDirection),
                  },
                  {
                    id: 'status',
                    label: 'وضعیت',
                    value: status,
                    options: STATUS_FILTERS,
                    onChange: (v) => setStatus(v as '' | VatEntryStatus),
                  },
                ]}
                toolbarExtra={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>از تاریخ</span>
                    <JalaliDateInput value={fromDate} onChange={setFromDate} />
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>تا تاریخ</span>
                    <JalaliDateInput value={toDate} onChange={setToDate} />
                  </div>
                }
                columns={columns}
                rows={rows}
                rowKey={(r) => r.id}
                rowTone={(r) =>
                  r.status === 'posted' ? 'success' : r.status === 'reversed' ? 'warn' : 'neutral'
                }
                zebra
                emptyState={<V2EmptyState icon="info" title="ثبت مالیات بر ارزش افزوده‌ای نیست" />}
              />
            ) : null}

            <V2Card>
              <V2CardHeader
                icon="shield"
                title="صورتحساب‌های سامانه مودیان"
                subtitle="ارسال صورتحساب الکترونیکی، دریافت شناسه یکتا و استعلام کارپوشه"
                actions={<V2Badge tone="success">فعال</V2Badge>}
              />
              <div
                style={{
                  padding: '12px 16px',
                  fontSize: 13,
                  lineHeight: 1.8,
                  color: 'var(--sk-fg)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div>
                  سامانه مودیان با کلید نامتقارن RSA و تولید خودکار شناسه مالیاتی ۲۲ کاراکتری فعال
                  است. برای ارسال صورتحساب‌ها، ثبت کلید و استعلام وضعیت به صفحه مودیان مراجعه کنید.
                </div>
                <Link className="sk-btn sk-btn--primary sk-btn--sm" to={`${base}/finance/modian`}>
                  ورود به سامانه مودیان
                </Link>
              </div>
            </V2Card>
          </div>

          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <V2Card>
              <V2CardHeader
                icon="invoice"
                title="خلاصهٔ ارزش افزودهٔ دوره"
                subtitle={`خلاصهٔ ثبت‌های قطعی · ${rangeLabel} · ${amountUnitLabel(unit)}`}
              />
              {summary.status === 'loading' ? <LoadingState /> : null}
              {summary.status === 'error' ? (
                <StatePanel
                  variant={summary.error.status === 403 ? 'permission_denied' : 'network_error'}
                  title={
                    summary.error.status === 403 ? 'دسترسی ندارید' : 'خلاصهٔ مالیات قابل نمایش نیست'
                  }
                />
              ) : null}
              {totals ? (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '12px 14px' }}
                >
                  {summaryRow('ارزش افزودهٔ فروش', formatRialFa(totals.outputVat.toString(), unit))}
                  {summaryRow(
                    'اعتبار مالیاتی خرید',
                    totals.inputVat > 0n
                      ? `−${formatRialFa(totals.inputVat.toString(), unit)}`
                      : formatRialFa('0', unit),
                    'var(--sk-credit)',
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 8,
                      paddingTop: 9,
                      borderTop: '1px dashed var(--sk-divider)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      {totals.payable < 0n ? 'مازاد اعتبار مالیاتی' : 'قابل پرداخت'}
                    </span>
                    <span className="sk-num" style={{ fontSize: 18, fontWeight: 800 }}>
                      {formatRialFa(
                        (totals.payable < 0n ? -totals.payable : totals.payable).toString(),
                        unit,
                      )}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>
                    مأخذ فروش {formatRialFa(totals.outputBase.toString(), unit)} · مأخذ خرید{' '}
                    {formatRialFa(totals.inputBase.toString(), unit)} ({amountUnitLabel(unit)})
                  </div>
                </div>
              ) : null}
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.8,
                  color: 'var(--sk-fg-muted)',
                  padding: '9px 14px',
                  borderTop: '1px dashed var(--sk-divider)',
                }}
              >
                برای تسلیم رسمی اظهارنامه و ثبت در سامانه مالیاتی، از برگه «اظهارنامه‌های فصلی و
                تسلیم» استفاده کنید.
              </div>
            </V2Card>
          </div>
        </div>
      ) : null}

      {/* VIEW 2: QUARTERLY VAT FILING (اظهارنامه‌های فصلی) */}
      {activeTab === 'filings' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <V2Card>
            <V2CardHeader
              icon="invoice"
              title="اظهارنامه‌های دوره‌ای مالیات بر ارزش افزوده"
              subtitle="محاسبه خودکار مأخذ و مالیات، تسلیم به سازمان امور مالیاتی و ثبت پرداخت فیش"
            />
            {filingsResult.status === 'loading' ? <LoadingState /> : null}
            {filingsResult.status === 'error' ? (
              <StatePanel
                variant={filingsResult.error.status === 403 ? 'permission_denied' : 'network_error'}
                title="خطا در دریافت فهرست اظهارنامه‌ها"
              />
            ) : null}
            {filingsResult.status === 'ready' && filingsResult.data.items.length === 0 ? (
              <div style={{ padding: 20 }}>
                <V2EmptyState
                  icon="invoice"
                  title="هنوز هیچ اظهارنامه فصلی تهیه نشده است"
                  action={
                    <V2Button
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        if (years.status === 'ready' && years.data.items[0]) {
                          setPrepareFiscalYearId(years.data.items[0].fiscalYearId);
                        }
                        handleQuarterChange(1, 1403);
                        setShowPrepareModal(true);
                      }}
                    >
                      تهیه اولین اظهارنامه فصلی
                    </V2Button>
                  }
                />
              </div>
            ) : null}
            {filingsResult.status === 'ready' && filingsResult.data.items.length > 0 ? (
              <div className="sk-table-scroll">
                <table className="sk-table sk-table--compact">
                  <thead>
                    <tr>
                      <th>دوره مالیاتی</th>
                      <th>بازه محاسباتی</th>
                      <th>مهلت تسلیم</th>
                      <th style={{ textAlign: 'end' }}>مأخذ فروش / خرید</th>
                      <th style={{ textAlign: 'end' }}>مالیات فروش / اعتبار</th>
                      <th style={{ textAlign: 'end' }}>مبلغ قابل پرداخت</th>
                      <th>وضعیت</th>
                      <th>اقدامات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filingsResult.data.items.map((f) => (
                      <tr key={f.filingId}>
                        <td>
                          <strong>
                            {f.yearJalali} — {QUARTER_NAMES[f.quarter] ?? `فصل ${f.quarter}`}
                          </strong>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {displayJalaliDate(f.periodStart)} تا {displayJalaliDate(f.periodEnd)}
                        </td>
                        <td style={{ fontSize: 12 }}>{displayJalaliDate(f.filingDeadline)}</td>
                        <td className="sk-num" style={{ textAlign: 'end', fontSize: 12 }}>
                          <div>{formatRialFa(f.salesTaxableBaseRial, unit)}</div>
                          <div style={{ color: 'var(--sk-fg-muted)' }}>
                            {formatRialFa(f.purchaseTaxableBaseRial, unit)}
                          </div>
                        </td>
                        <td className="sk-num" style={{ textAlign: 'end', fontSize: 12 }}>
                          <div>{formatRialFa(f.salesVatRial, unit)}</div>
                          <div style={{ color: 'var(--sk-credit)' }}>
                            −{formatRialFa(f.purchaseCreditRial, unit)}
                          </div>
                        </td>
                        <td className="sk-num" style={{ textAlign: 'end', fontWeight: 700 }}>
                          {formatRialFa(f.payableVatRial, unit)}
                        </td>
                        <td>
                          <V2Badge
                            tone={
                              f.status === 'paid'
                                ? 'success'
                                : f.status === 'submitted'
                                  ? 'info'
                                  : 'warn'
                            }
                          >
                            {f.status === 'paid'
                              ? 'پرداخت‌شده'
                              : f.status === 'submitted'
                                ? 'تسلیم سازمان'
                                : 'پیش‌نویس'}
                          </V2Badge>
                          {f.submissionReceiptNumber ? (
                            <div
                              style={{ fontSize: 11, color: 'var(--sk-fg-muted)', marginTop: 2 }}
                            >
                              رهگیری: {f.submissionReceiptNumber}
                            </div>
                          ) : null}
                          {f.paymentReference ? (
                            <div style={{ fontSize: 11, color: 'var(--sk-credit)', marginTop: 2 }}>
                              فیش: {f.paymentReference}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {f.status === 'draft' ? (
                            <button
                              type="button"
                              className="sk-btn sk-btn--primary sk-btn--sm"
                              onClick={() => {
                                setSubmitFilingTarget(f);
                                setReceiptNumber('');
                              }}
                            >
                              تسلیم به سازمان
                            </button>
                          ) : f.status === 'submitted' ? (
                            <button
                              type="button"
                              className="sk-btn sk-btn--secondary sk-btn--sm"
                              onClick={() => {
                                setPayFilingTarget(f);
                                setPaymentReference('');
                              }}
                            >
                              ثبت فیش پرداخت
                            </button>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--sk-success)' }}>
                              تکمیل‌شده
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </V2Card>
        </div>
      ) : null}

      {/* MODAL 1: PREPARE FILING */}
      {showPrepareModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 540,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowPrepareModal(false)} />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              تهیه اظهارنامه فصلی ارزش افزوده
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              سیستم به صورت خودکار مأخذ مالیاتی فروش، اعتبار خرید و مانده قابل پرداخت را از اسناد
              قطعی استخراج می‌کند.
            </p>

            <V2FormGrid>
              <V2FormField label="سال مالی" required width="medium">
                <select
                  className="sk-select"
                  value={prepareFiscalYearId}
                  onChange={(e) => setPrepareFiscalYearId(e.target.value)}
                >
                  <option value="">انتخاب سال</option>
                  {years.status === 'ready' &&
                    years.data.items.map((y) => (
                      <option key={y.fiscalYearId} value={y.fiscalYearId}>
                        {y.name}
                      </option>
                    ))}
                </select>
              </V2FormField>

              <V2FormField label="سال خورشیدی" required width="short">
                <input
                  type="number"
                  className="sk-input ltr"
                  value={prepareYearJalali}
                  onChange={(e) =>
                    handleQuarterChange(prepareQuarter, parseInt(e.target.value, 10) || 1403)
                  }
                />
              </V2FormField>

              <V2FormField label="فصل مالیاتی" required width="medium">
                <select
                  className="sk-select"
                  value={prepareQuarter}
                  onChange={(e) =>
                    handleQuarterChange(parseInt(e.target.value, 10), prepareYearJalali)
                  }
                >
                  <option value={1}>بهار (سه ماهه اول)</option>
                  <option value={2}>تابستان (سه ماهه دوم)</option>
                  <option value={3}>پاییز (سه ماهه سوم)</option>
                  <option value={4}>زمستان (سه ماهه چهارم)</option>
                </select>
              </V2FormField>

              <V2FormField label="شروع دوره" required width="medium">
                <JalaliDateInput value={preparePeriodStart} onChange={setPreparePeriodStart} />
              </V2FormField>

              <V2FormField label="پایان دوره" required width="medium">
                <JalaliDateInput value={preparePeriodEnd} onChange={setPreparePeriodEnd} />
              </V2FormField>

              <V2FormField label="مهلت تسلیم" required width="medium">
                <JalaliDateInput
                  value={prepareFilingDeadline}
                  onChange={setPrepareFilingDeadline}
                />
              </V2FormField>
            </V2FormGrid>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                disabled={busy}
                onClick={() => setShowPrepareModal(false)}
              >
                انصراف
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy}
                onClick={() => void onPrepareFiling()}
              >
                {busy ? 'در حال استخراج و محاسبه...' : 'محاسبه و ایجاد پیش‌نویس اظهارنامه'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL 2: SUBMIT FILING (تسلیم به سازمان) */}
      {submitFilingTarget ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 480,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setSubmitFilingTarget(null)} />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              تسلیم اظهارنامه به سازمان امور مالیاتی
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              دوره: {submitFilingTarget.yearJalali} — {QUARTER_NAMES[submitFilingTarget.quarter]} |
              مبلغ قابل پرداخت: {formatRialFa(submitFilingTarget.payableVatRial, unit)}
            </p>

            <V2FormField label="شماره رهگیری / قبض سازمان مالیاتی" required width="wide">
              <input
                className="sk-input ltr"
                placeholder="مثلاً ۱۲۳۴۵۶۷۸۹۰"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
              />
            </V2FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                disabled={busy}
                onClick={() => setSubmitFilingTarget(null)}
              >
                انصراف
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy || !receiptNumber.trim()}
                onClick={() => void onSubmitFiling()}
              >
                {busy ? 'در حال ثبت...' : 'ثبت قطعی تسلیم اظهارنامه'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL 3: PAY FILING (پرداخت قبض مالیات) */}
      {payFilingTarget ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 480,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setPayFilingTarget(null)} />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              ثبت پرداخت مالیات بر ارزش افزوده
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              مبلغ واریزی: {formatRialFa(payFilingTarget.payableVatRial, unit)} | رهگیری تسلیم:{' '}
              {payFilingTarget.submissionReceiptNumber}
            </p>

            <V2FormField label="شناسه پرداخت / شماره فیش بانکی" required width="wide">
              <input
                className="sk-input ltr"
                placeholder="مثلاً ۹۸۷۶۵۴۳۲۱"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </V2FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                disabled={busy}
                onClick={() => setPayFilingTarget(null)}
              >
                انصراف
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy || !paymentReference.trim()}
                onClick={() => void onPayFiling()}
              >
                {busy ? 'در حال ثبت...' : 'ثبت پرداخت مالیات'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
